/*
 * Gallery Neighbour Warming + Hydration
 * Slides other than the active one render src-less with loading="lazy"
 * (snippets/media.liquid + snippets/image-element.liquid): they carry
 * data-src/data-srcset and cost zero bytes until hydrated.
 *
 * This script:
 *  - hydrates the SELECTED slide immediately whenever selection changes
 *    (thumbnail jumps must never wait for an idle callback)
 *  - hydrates + eager-loads the neighbours once the browser is idle, so
 *    single-step swipes always have the next slide loaded
 *  - discovers galleries dynamically: quick-shop modals fetch product HTML
 *    after DOMContentLoaded, so a body-level observer registers any
 *    [data-product-photos] added later
 *
 * It is the ONLY hydration path for products without image sets
 * (variant-image-filter.js exits early when no data-group exists).
 *
 * THEME UPDATE NOTE:
 * If theme is updated, re-add this line to layout/theme.liquid in <head>,
 * between mobile-thumb-autohide.js and theme.js:
 *   <script src="{{ 'gallery-warm-neighbours.js' | asset_url }}" defer="defer"></script>
 */

(function() {
  'use strict';

  var DEBOUNCE = 50;
  var IDLE_TIMEOUT = 2000;
  var idle = window.requestIdleCallback || function(cb) { return setTimeout(cb, 300); };

  function eager(slide) {
    if (!slide) return;
    slide.querySelectorAll('img[loading="lazy"], img[data-src]').forEach(function(img) {
      // Hydrate src-less slides first. srcset before src.
      var srcset = img.getAttribute('data-srcset');
      if (srcset) { img.srcset = srcset; img.removeAttribute('data-srcset'); }
      var src = img.getAttribute('data-src');
      if (src) { img.src = src; img.removeAttribute('data-src'); }
      // Setting .loading on a not-yet-loaded lazy image triggers the fetch immediately
      img.loading = 'eager';
    });
  }

  function setup(slideshow) {
    if (slideshow.__warmSetup) return;
    slideshow.__warmSetup = true;

    var timer = null;

    function selected() {
      return slideshow.querySelector('.flickity-slider .product-main-slide.is-selected');
    }

    function warmNeighbours() {
      var current = selected();
      if (!current) return;
      eager(current); // a variant switch can land on a slide that was still lazy
      eager(current.previousElementSibling);
      eager(current.nextElementSibling);
    }

    function schedule() {
      // The selected slide hydrates NOW — a thumbnail jump to a far slide must
      // not sit blank behind the debounce + idle delay. Neighbours can wait.
      eager(selected());
      clearTimeout(timer);
      timer = setTimeout(function() {
        idle(warmNeighbours, { timeout: IDLE_TIMEOUT });
      }, DEBOUNCE);
    }

    schedule();

    // Same pattern as mobile-thumb-autohide.js: subtree is required because Flickity
    // wraps slides in .flickity-slider, childList catches the destroy/recreate on
    // variant change. attributeFilter is ['class'] so our own src/loading writes
    // can't retrigger the observer.
    new MutationObserver(schedule).observe(slideshow, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      childList: true
    });
  }

  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-product-photos]').forEach(setup);

    // Quick-shop modals (theme.preloadProductModal) insert product markup after
    // DOMContentLoaded — register their galleries as they appear.
    new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        m.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-product-photos]')) {
            setup(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('[data-product-photos]').forEach(setup);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
